import type { BlogPost } from '../types/blog';

interface Frontmatter {
  title: string;
  description: string;
  date: string;
  category: string;
  tags: string[];
  author: string;
  readingTime: string;
  avatar?: string;
  [key: string]: string | string[] | undefined;
}

function parseFrontmatter(content: string): { frontmatter: Frontmatter, content: string } {
  const frontmatter: Frontmatter = {
    title: '',
    description: '',
    date: '',
    category: '',
    tags: [],
    author: '',
    readingTime: '',
  };
  let markdownContent = content;

  // Check if content starts with frontmatter
  if (content.startsWith('---')) {
    const endOfFrontmatter = content.indexOf('---', 3);
    if (endOfFrontmatter !== -1) {
      const frontmatterText = content.slice(3, endOfFrontmatter);
      markdownContent = content.slice(endOfFrontmatter + 3).trim();

      // Parse YAML-like frontmatter
      frontmatterText.split('\n').forEach(line => {
        const [key, ...valueParts] = line.split(':');
        if (key && valueParts.length > 0) {
          const value = valueParts.join(':').trim();
          const trimmedKey = key.trim();
          // Handle arrays
          if (value.startsWith('[') && value.endsWith(']')) {
            frontmatter[trimmedKey] = value
              .slice(1, -1)
              .split(',')
              .map(item => item.trim().replace(/['"]/g, ''));
          } else {
            frontmatter[trimmedKey] = value;
          }
        }
      });
    }
  }

  return { frontmatter, content: markdownContent };
}

// List of blog post slugs
const BLOG_POSTS = [
  'launch-week-q1-25-day1',
  'launch-week-q1-25-day2',
  'launch-week-q1-25-day3',
  'launch-week-q1-25-day4',
  'launch-week-q1-25-day5',
  'sdks-for-ai-not-humans',
  'notebooks-app-builders-ide',
  'no-code-data-stack-learnings',
  'machines-to-functions',
  'llms-more-than-text2sql',
  'intro-to-ibis',
  'hidden-price-tag',
  'electric-tea-kettle',
  'excel-reactive-dag',
  'easier-data-apps',
  'earthquakes-dashboard',
  'csvs-not-dbs',
  'config-driven-analytics',
  'computational-graph',
  'company-values',
  'cholesterol-trends',
  'broswer-based-data-apps',
  'b2a-analytics',
  'aws-iceberg-simplicity',
  'analytics-stack-code',
  'analytics-function-in-2025',
  'ai-chatbot-analysis',
  'interactive-html-export-preswald',
  'implicit-rendering'
];

export async function getBlogPosts(): Promise<BlogPost[]> {
  try {
    const posts = await Promise.all(
      BLOG_POSTS.map(async (slug) => {
        try {
          const response = await fetch(`/blog-posts/${slug}/index.md`);
          if (!response.ok) {
            return null;
          }
          const content = await response.text();
          const { frontmatter, content: markdownContent } = parseFrontmatter(content);
          
          return {
            id: slug,
            slug,
            title: frontmatter.title,
            description: frontmatter.description,
            content: markdownContent,
            date: frontmatter.date,
            category: frontmatter.category,
            tags: frontmatter.tags || [],
            author: frontmatter.author,
            readingTime: frontmatter.readingTime,
            image: `/blog-posts/${slug}/cover.png`,
            avatar: frontmatter.avatar,
          } as BlogPost;
        } catch {
          return null;
        }
      })
    );

    return posts.filter((post): post is BlogPost => post !== null);
  } catch {
    return [];
  }
}

export async function getBlogPost(slug: string): Promise<BlogPost | null> {
  try {
    const response = await fetch(`/blog-posts/${slug}/index.md`);
    
    if (!response.ok) {
      return null;
    }

    const content = await response.text();
    const { frontmatter, content: markdownContent } = parseFrontmatter(content);

    return {
      id: slug,
      slug,
      title: frontmatter.title,
      description: frontmatter.description,
      content: markdownContent,
      date: frontmatter.date,
      category: frontmatter.category,
      tags: frontmatter.tags || [],
      author: frontmatter.author,
      readingTime: frontmatter.readingTime,
      image: `/blog-posts/${slug}/cover.png`,
      avatar: frontmatter.avatar,
    } as BlogPost;
  } catch {
    return null;
  }
}

export async function getImageUrl(slug: string, imageName: string): Promise<string> {
  try {
    // Handle both cover images and embedded images
    const isCoverImage = imageName === 'cover.png';
    return `/blog-posts/${slug}/${isCoverImage ? '' : 'embedded/'}${imageName}`;
  } catch {
    return ''; // Return empty string for missing images
  }
} 